import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api, apiUrls } from "../utils/api";
import { defaultFields, localDefaultFields } from "../pages/Settings/components/GanttConfigField/config";
import {
  columnKeys,
  getColumnId,
  groups,
  SettingColumn,
  SettingGroups,
} from "../pages/Settings/components/Columns/configs";
import { RootStore } from "./index";
import { AccessEntries } from "../utils/accessEntries";
import { UserRight } from "../types/user";
import { setWeekStart } from "./localizations";

const fetchSetting = createAsyncThunk("Setting/PersonalView/fetch", async (token: string) => {  
  return Promise.all([api.get(apiUrls.PersonalViewSettings, null, token), api.get(apiUrls.GetUserData, null, token), api.get(apiUrls.SyncLogStatus, null, token)
  ]).then(([res1, res2, res3]) => {
    return Promise.all([res1.json(), res2.json(), res3.json()]);
  });
});
type ColumnRecord = Record<string, SettingColumn>;
function getColumns(groupName: SettingGroups, configColumns: ColumnRecord) {
  // clean column settings
  if (configColumns) {
    const resConfig: ColumnRecord = {};
    groups[groupName].forEach((i) => {
      const key = getColumnId(i);
      const userConfig = configColumns[key];
      if (userConfig) {
        resConfig[key] = {} as SettingColumn;
        const column = resConfig[key];
        // copy only properties that needed
        columnKeys.forEach((k) => {
          // check if user has saved config
          if (k in userConfig) {
            // @ts-expect-error ignore
            column[k] = userConfig[k];
          } else if (i[k] !== undefined) {
            // @ts-expect-error ignore
            column[k] = i[k];
          }
        });
      } else {
        resConfig[key] = {
          ...i,
        };
      }
    });
    return resConfig;
  }
  const columns: ColumnRecord = {};
  groups[groupName].forEach((i) => {
    const key = getColumnId(i);
    columns[key] = { ...i };
  });
  return columns;
}

export type AccessRight = {
  Name: string;
  AccessLevels: string[];
};

export type User = {
  Name: string;
};
export enum SyncErrorType {
  None,
  ResourceError,
  GeneralError
}

const initialState = {
  fetching: false,
  authorized: false,
  user: {
    isAdministrator: false,
    erpConnectionType: null,
    companyId : null,
    companyName: null,
    userId: null,
    userName: null,
    accessRights: {} as Record<AccessEntries, UserRight>,
  },
  screenLayoutSingleViewConfig: null,
  screenLayoutMultiViewConfig: null,
  screenLayoutDeptViewConfig: null,
  canSaveCompanyScreenLayout: false,
  canSaveUserScreenLayout: false,
  lastSyncData: {
    hasError: SyncErrorType.None,
    statusTooltip: ""
  }
};

const settingSlice = createSlice({
  name: "setting",
  initialState,
  reducers: {
    logout: () => {
      return initialState;
    },
  },
  extraReducers: 
  (builder) => {
      builder.addCase(fetchSetting.pending, (state) => {
        state.fetching = true;
      });
    builder.addCase(fetchSetting.rejected, (state) => {
      state.fetching = false;
      return initialState;
    });
    builder.addCase(fetchSetting.fulfilled, (state, action) => {
      if (!action.payload) {
        return initialState;
      }

      const [settings, user, lastSyncData] = action.payload;
      const {
        screenLayoutSingleViewConfig,
        screenLayoutMultiViewConfig,
        screenLayoutDeptViewConfig,
        generalProjectSettings,
        ...restPayload
      } = settings;

      const multiViewConfig = {
        ...defaultFields,
        ...localDefaultFields[SettingGroups.Multiple],
        ...JSON.parse(screenLayoutMultiViewConfig || "{}"),
      };
      const singleViewConfig = {
        ...defaultFields,
        ...localDefaultFields[SettingGroups.Single],
        ...JSON.parse(screenLayoutSingleViewConfig || "{}"),
      };
      const depViewConfig = {
        ...defaultFields,
        ...localDefaultFields[SettingGroups.Department],
        ...JSON.parse(screenLayoutDeptViewConfig || "{}"),
      };

      const tempProjectSettings = JSON.parse(generalProjectSettings || null);
      setWeekStart(tempProjectSettings?.StartDayOfWeek);   

      return {
        ...state,
        ...restPayload,
        user: {
          isAdministrator: user.isAdministrator,
          erpConnectionType: user.erpConnectionType,
          companyId : user.companyId,
          companyName: user.companyName,
          userId: user.userId,
          userName: user.userName,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          accessRights: (user?.userAccessRights as any[])?.reduce((a, b) => {
            a[b.AccessRight] = b;
            return a;
          }, {}),
        },
        generalProjectSettings: JSON.parse(generalProjectSettings || "{}"),
        screenLayoutSingleViewConfig: {
          ...singleViewConfig,
          columns: getColumns(SettingGroups.Single, singleViewConfig.columns),          
        },
        screenLayoutMultiViewConfig: {
          ...multiViewConfig,
          columns: getColumns(SettingGroups.Multiple, multiViewConfig.columns),
          showTreeStructure: multiViewConfig.showTreeStructure !== false,
        },
        screenLayoutDeptViewConfig: {
          ...depViewConfig,
          columns: getColumns(SettingGroups.Department, depViewConfig.columns),
        },
        lastSyncData: lastSyncData,
        fetching: false,
        authorized: true,
      };
    });
  },
});
export const settingReducer = settingSlice.reducer;
export const departmentSettingSelector = (state: RootStore) => state.setting.screenLayoutDeptViewConfig;
export const singleSettingSelector = (state: RootStore) => state.setting.screenLayoutSingleViewConfig;
export const multiViewSettingSelector = (state: RootStore) => state.setting.screenLayoutMultiViewConfig;
export const userRightsSelector = (state: RootStore) => state.setting.user?.accessRights;
export const userSelector = (state: RootStore) => state.setting.user;


export const settingActions = {
  fetchSetting,
  logOut: settingSlice.actions.logout,
};
